/**
 * BackendTaskService
 *
 * Service for managing AI generation tasks in Firestore.
 * Collection path: projects/{projectId}/backend_tasks
 *
 * Compatible with legacy frontend/src/contexts/TaskContext.js which listens
 * to the same collection for real-time task status updates.
 *
 * Features:
 * - Create tasks with 'running' status
 * - Update task status (complete, fail)
 * - Real-time subscriptions for task progress
 * - Type-safe API
 */

/**
 * Debug mode - set to true to see detailed logs
 */
const DEBUG = import.meta.env.VITE_DEBUG_CLAUDE_AGENT === 'true' || true; // Enable by default

/**
 * Debug logger - only logs when DEBUG is true
 */
function debugLog(label: string, data: unknown): void {
  if (!DEBUG) return;
  console.group(`[BackendTaskService] 🔍 DEBUG: ${label}`);
  if (typeof data === 'string') {
    console.log(data);
  } else {
    try {
      console.log(JSON.stringify(data, null, 2));
    } catch {
      console.log(data);
    }
  }
  console.groupEnd();
}

import {
  collection,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  Unsubscribe,
  DocumentSnapshot,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { backendTaskConverter } from '../converters/backendTaskConverter';
import type {
  ContentAgentTask,
  CreateTaskInput,
  UpdateTaskInput,
  BlockUpdateOutput,
} from '../types';

/**
 * BackendTaskService
 *
 * Manages AI generation tasks in Firestore for progress tracking.
 */
export class BackendTaskService {
  private readonly collectionName = 'backend_tasks';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  /**
   * Get the collection path for a project's backend tasks
   */
  private getCollectionPath(projectId: string): string {
    const path = `projects/${projectId}/${this.collectionName}`;
    debugLog('PATH - getCollectionPath', {
      projectId,
      projectIdLength: projectId?.length,
      projectIdTrimmed: projectId?.trim(),
      hasLeadingSpace: projectId?.startsWith(' '),
      hasTrailingSpace: projectId?.endsWith(' '),
      collectionName: this.collectionName,
      fullPath: path,
      pathCharCodes: path.substring(0, 20).split('').map(c => c.charCodeAt(0)),
    });
    return path;
  }

  /**
   * Create a new task with 'running' status
   *
   * @param input - Task creation input
   * @returns Created task with ID
   */
  async create(input: CreateTaskInput): Promise<ContentAgentTask> {
    try {
      console.log('[BackendTaskService] ========== CREATING TASK ==========');
      debugLog('CREATE - Input', {
        action: input.action,
        userId: input.userId,
        projectId: input.projectId,
        strategyId: input.strategyId,
        blockId: input.blockId,
      });

      const collectionPath = this.getCollectionPath(input.projectId);
      debugLog('CREATE - Collection path', collectionPath);

      const collectionRef = collection(this.firestore, collectionPath);
      const docRef = doc(collectionRef).withConverter(backendTaskConverter);

      debugLog('CREATE - Document ref path', docRef.path);

      const task: ContentAgentTask = {
        id: docRef.id,
        status: 'running',
        action: input.action,
        description: input.description,
        userId: input.userId,
        projectId: input.projectId,
        strategyId: input.strategyId,
        blockId: input.blockId,
        createdAt: new Date(),
      };

      debugLog('CREATE - Task object', {
        id: task.id,
        status: task.status,
        action: task.action,
        projectId: task.projectId,
      });

      await setDoc(docRef, task);

      console.log('[BackendTaskService] ✅ Task created:', task.id);
      console.log('[BackendTaskService] 📍 Full path:', docRef.path);
      return task;
    } catch (error) {
      console.error('[BackendTaskService] ❌ Task creation failed');
      debugLog('CREATE - Error', error);
      CoreFirebaseService.getInstance().logError(error, 'BackendTaskService.create');
      throw error;
    }
  }

  /**
   * Get a task by ID
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @returns Task or null if not found
   */
  async get(projectId: string, taskId: string): Promise<ContentAgentTask | null> {
    try {
      const docRef = doc(
        this.firestore,
        this.getCollectionPath(projectId),
        taskId
      ).withConverter(backendTaskConverter);

      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BackendTaskService.get');
      throw error;
    }
  }

  /**
   * Update task status and data
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @param data - Update data
   */
  async update(
    projectId: string,
    taskId: string,
    data: UpdateTaskInput
  ): Promise<void> {
    try {
      debugLog('UPDATE - Params', {
        projectId,
        taskId,
        status: data.status,
        hasResult: !!data.result,
        hasError: !!data.error,
      });

      const collectionPath = this.getCollectionPath(projectId);
      const docRef = doc(
        this.firestore,
        collectionPath,
        taskId
      ).withConverter(backendTaskConverter);

      debugLog('UPDATE - Document path', docRef.path);

      const updateData: Partial<ContentAgentTask> = {
        ...data,
        updatedAt: new Date(),
      };

      await setDoc(docRef, updateData as ContentAgentTask, { merge: true });

      console.log('[BackendTaskService] ✅ Task updated:', taskId, '→', data.status);
    } catch (error) {
      console.error('[BackendTaskService] ❌ Task update failed');
      debugLog('UPDATE - Error', error);
      CoreFirebaseService.getInstance().logError(error, 'BackendTaskService.update');
      throw error;
    }
  }

  /**
   * Mark task as completed with result
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @param result - AI generation result
   */
  async complete(
    projectId: string,
    taskId: string,
    result: BlockUpdateOutput
  ): Promise<void> {
    debugLog('COMPLETE - Params', {
      projectId,
      taskId,
      blocksInResult: result.onboardingStrategySequence?.length || 0,
    });
    await this.update(projectId, taskId, {
      status: 'completed',
      result,
    });
    console.log('[BackendTaskService] ✅ Task completed:', taskId);
  }

  /**
   * Mark task as failed with error message
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @param error - Error message
   */
  async fail(projectId: string, taskId: string, error: string): Promise<void> {
    debugLog('FAIL - Params', {
      projectId,
      taskId,
      error,
    });
    await this.update(projectId, taskId, {
      status: 'error',
      error,
    });
    console.error('[BackendTaskService] ❌ Task failed:', taskId, error);
  }

  /**
   * Cancel a running task
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @param reason - Cancellation reason
   */
  async cancel(
    projectId: string,
    taskId: string,
    reason: string = 'User cancelled'
  ): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        this.getCollectionPath(projectId),
        taskId
      ).withConverter(backendTaskConverter);

      const updateData: Partial<ContentAgentTask> = {
        status: 'cancelled',
        cancelReason: reason,
        cancelledAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(docRef, updateData as ContentAgentTask, { merge: true });

      console.log('[BackendTaskService] Task cancelled:', taskId);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'BackendTaskService.cancel');
      throw error;
    }
  }

  /**
   * Subscribe to real-time task updates
   *
   * @param projectId - Project ID
   * @param taskId - Task ID
   * @param onNext - Callback for task updates
   * @param onError - Callback for errors
   * @returns Unsubscribe function
   */
  subscribe(
    projectId: string,
    taskId: string,
    onNext: (task: ContentAgentTask | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    try {
      const docRef = doc(
        this.firestore,
        this.getCollectionPath(projectId),
        taskId
      ).withConverter(backendTaskConverter);

      return onSnapshot(
        docRef,
        (snapshot: DocumentSnapshot<ContentAgentTask>) => {
          if (snapshot.exists()) {
            onNext(snapshot.data());
          } else {
            onNext(null);
          }
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(
            error,
            'BackendTaskService.subscribe'
          );
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'BackendTaskService.subscribe.setup'
      );
      throw error;
    }
  }
}

export default BackendTaskService;
